/**
 * Ghost Booking — typed client for the frozen contract in
 * deploy/caldiy/GHOST-BOOKING-PLAN.md ("Контракт API"). Backend lives in
 * h/booking/ (Flask) and is reached through the same origin via the nginx
 * `location /booking/api/` proxy (see deploy/caldiy/nginx-book.knopka.click.conf),
 * the same way apps/web/modules/bookings/hooks/useKnopkaEventTranslation.ts
 * already calls `/booking/api/public/event-translation`.
 *
 * Keep the username mask and reserved-path list here in sync with the
 * backend's own copy — the plan requires an explicit constant on both sides,
 * not a shared import (different languages/repos).
 */

/** `^[a-zA-Z0-9_]{5,32}$` — plan section "Правила и защиты", rule 1. */
export const GHOST_USERNAME_PATTERN = /^[a-zA-Z0-9_]{5,32}$/;

/**
 * First path segment reserved for real cal.diy routes — never a ghost
 * candidate even when it happens to match the username mask. Sourced from:
 *  - real top-level folders under /Users/dimaneuron/cal.diy/apps/web/app
 *    (route groups and dynamic segments excluded — those can't collide with
 *    a literal path segment),
 *  - apps/web/next.config.ts rewrite `source` roots (login -> /auth/login,
 *    embed, routing, routing-forms, forms, success, cancel, support, call, org).
 * Plan explicitly calls out: auth, settings, apps, booking, event-types,
 * teams, api, embed, signup, login.
 */
export const GHOST_RESERVED_ROOT_PATHS: ReadonlySet<string> = new Set([
  "api",
  "auth",
  "apps",
  "availability",
  "enterprise",
  "getting-started",
  "maintenance",
  "more",
  "onboarding",
  "payment",
  "refer",
  "settings",
  "signup",
  "teams",
  "upgrade",
  "video",
  "booking",
  "bookings",
  "members",
  "booking-successful",
  "d",
  "team",
  "reschedule",
  "icons",
  "login",
  "embed",
  "routing-forms",
  "routing",
  "forms",
  "success",
  "cancel",
  "support",
  "call",
  "org",
]);

/**
 * Ghost view only ever applies to the root path (`/miakam`), never to
 * subpaths (`/miakam/30min`) — plan rule 2 "Только корневой путь".
 */
export function parseGhostUsernameFromPathname(pathname: string | null | undefined): string | null {
  const clean = (pathname || "").replace("%20", "-").split(/[?#]/)[0] || "";
  const segments = clean.split("/").filter(Boolean);
  if (segments.length !== 1) return null;
  const candidate = segments[0];
  if (!GHOST_USERNAME_PATTERN.test(candidate)) return null;
  if (GHOST_RESERVED_ROOT_PATHS.has(candidate.toLowerCase())) return null;
  return candidate;
}

export type GhostProfileKind = "user" | "bot" | "channel" | "unknown" | "missing";

/**
 * Ghost page is shown for real Telegram entities of these kinds. Free
 * (`missing`) and unrecognized (`unknown`) usernames keep the plain cal.diy
 * 404 — keep in sync with BOOKABLE_KINDS in h/booking/telegram_profile_service.py.
 */
export const GHOST_BOOKABLE_KINDS: readonly GhostProfileKind[] = ["user", "bot", "channel"];

/** Raw shape of `GET /booking/api/public/telegram-profile?username=`. */
export type TelegramProfileApiResponse = {
  ok: true;
  exists: boolean;
  kind: GhostProfileKind;
  username: string;
  display_name?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  claimed: boolean;
  cached?: boolean;
  fetched_at?: string;
};

export type TelegramProfileApiError = {
  ok: false;
  error: string;
};

export type GhostProfile = {
  username: string;
  displayName: string | null;
  bio: string | null;
  avatarUrl: string | null;
};

/**
 * `available` is the only branch that renders the booking UI. Everything
 * else — exists:false, claimed:true, a non-bookable kind (missing/unknown),
 * a network error, a
 * non-2xx status, or a malformed body — collapses to `unavailable`, which
 * the view renders as the plain cal.diy 404 (plan: "при exists:false или
 * ошибке API — показываем обычную 404 без мигания").
 */
export type GhostProfileResult = { status: "available"; profile: GhostProfile } | { status: "unavailable" };

export async function fetchGhostTelegramProfile(
  username: string,
  signal?: AbortSignal
): Promise<GhostProfileResult> {
  try {
    const response = await fetch(`/booking/api/public/telegram-profile?username=${encodeURIComponent(username)}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) return { status: "unavailable" };

    const data = (await response.json().catch(() => null)) as
      | TelegramProfileApiResponse
      | TelegramProfileApiError
      | null;
    if (!data || data.ok !== true) return { status: "unavailable" };
    if (!data.exists || data.claimed) return { status: "unavailable" };
    if (!GHOST_BOOKABLE_KINDS.includes(data.kind)) return { status: "unavailable" };

    return {
      status: "available",
      profile: {
        username: data.username || username,
        displayName: (data.display_name || "").trim() || null,
        bio: (data.bio || "").trim() || null,
        avatarUrl: data.avatar_url || null,
      },
    };
  } catch {
    return { status: "unavailable" };
  }
}

/** `GET /booking/api/public/telegram-avatar/<username>` — never hotlink Telegram's CDN directly. */
export function ghostTelegramAvatarUrl(username: string): string {
  return `/booking/api/public/telegram-avatar/${encodeURIComponent(username)}`;
}

export type GhostBookingLocale = "ru" | "en";

export type GhostBookingRequestInput = {
  username: string;
  /** UTC ISO string, e.g. "2026-09-07T10:00:00Z" — from knopkaGhostSlots. */
  slotStartUtcIso: string;
  /** Visitor's IANA timezone, e.g. "Europe/Moscow". */
  timezone: string;
  visitorName?: string | null;
  locale: GhostBookingLocale;
};

export type GhostBookingRequestResult =
  | { ok: true; requestId: number; telegramUrl: string }
  | { ok: false; error?: string };

/** `POST /booking/api/public/ghost-request` — server builds the t.me message text. */
export async function submitGhostBookingRequest(
  input: GhostBookingRequestInput,
  signal?: AbortSignal
): Promise<GhostBookingRequestResult> {
  const visitorName = (input.visitorName || "").trim().slice(0, 64) || undefined;
  try {
    const response = await fetch("/booking/api/public/ghost-request", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      signal,
      body: JSON.stringify({
        username: input.username,
        slot_start: input.slotStartUtcIso,
        tz: input.timezone,
        visitor_name: visitorName,
        locale: input.locale,
      }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.ok !== true || typeof data.telegram_url !== "string" || !data.telegram_url) {
      const error = data && typeof data.error === "string" ? data.error : undefined;
      return { ok: false, error };
    }
    return {
      ok: true,
      requestId: Number(data.request_id) || 0,
      telegramUrl: data.telegram_url,
    };
  } catch {
    return { ok: false };
  }
}
